import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";

import { getMyRoles } from "@/lib/roles.functions";

// PERF: the Supabase client is imported lazily (inside effects/handlers) so
// `@supabase/supabase-js` never lands in the main entry chunk. Behaviour is
// unchanged — the session simply resolves one microtask/chunk later.
const loadSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);

export type AppRole = "owner" | "admin" | "moderator" | "user";

const cacheKey = (userId: string) => `mm:roles:${userId}`;

function readCachedRoles(userId: string): AppRole[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(cacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AppRole[]) : null;
  } catch {
    return null;
  }
}

function writeCachedRoles(userId: string, roles: AppRole[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(cacheKey(userId), JSON.stringify(roles));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [rolesReady, setRolesReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      const supabase = await loadSupabase();
      if (!mounted) return;
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session ?? null);
      setReady(true);

      const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
        setSession(s);
      });
      unsubscribe = () => sub.subscription.unsubscribe();
      if (!mounted) unsubscribe();
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setRoles([]);
      setRolesReady(ready);
      return;
    }
    // Paint the correct navigation immediately on reload / tab restore, then
    // re-verify against the server.
    const cached = readCachedRoles(userId);
    if (cached) {
      setRoles(cached);
      setRolesReady(true);
    }

    let active = true;
    void (async () => {
      try {
        const me = await getMyRoles();
        if (!active) return;
        setRoles(me.roles as AppRole[]);
        writeCachedRoles(userId, me.roles as AppRole[]);
      } catch {
        // Fallback: read the user's own roles directly (RLS allows it).
        try {
          const supabase = await loadSupabase();
          const { data } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId);
          if (!active) return;
          const next = ((data ?? []) as { role: AppRole }[]).map((r) => r.role);
          setRoles(next);
          writeCachedRoles(userId, next);
        } catch {
          /* keep cached value */
        }
      } finally {
        if (active) setRolesReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [session?.user?.id, ready]);

  const user: User | null = session?.user ?? null;
  const isAdmin = roles.includes("owner") || roles.includes("admin");
  const isOwner = roles.includes("owner");
  const isStaff = isAdmin || roles.includes("moderator");

  return { ready, rolesReady, session, user, roles, isAdmin, isOwner, isStaff };
}

/** Borra todas las cachés de roles guardadas en esta pestaña. */
export function clearRoleCaches() {
  if (typeof window === "undefined") return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith("mm:roles:")) keys.push(k);
    }
    keys.forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/**
 * Cierra la sesión de forma resiliente: si el refresh token ya expiró o la red
 * falla, el `signOut` global puede lanzar — en ese caso limpiamos la sesión
 * local para que el usuario nunca quede "a medias" (UI de logueado sin token).
 */
export async function signOut() {
  clearRoleCaches();
  const supabase = await loadSupabase();
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch {
    try {
      // Fallback: elimina únicamente la sesión de este dispositivo.
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      /* último recurso: la recarga posterior descarta el estado en memoria */
    }
  }
}

export const SIGNED_OUT_FLAG = "mm:signed-out";

/**
 * Cierra sesión por completo y vuelve al inicio, incluso si el logout remoto
 * falla. Usa `location.replace` (recarga completa) para vaciar toda la caché
 * en memoria y no dejar la página privada en el historial.
 */
export async function signOutAndGoHome(to = "/") {
  try {
    await signOut();
  } finally {
    if (typeof window !== "undefined") {
      try {
        // Restos de sesión (incluida la de Google en este sitio).
        for (const store of [window.localStorage, window.sessionStorage]) {
          const keys: string[] = [];
          for (let i = 0; i < store.length; i++) {
            const k = store.key(i);
            if (k && (k.startsWith("sb-") || k.includes("auth-token") || k.startsWith("mm:roles:"))) keys.push(k);
          }
          keys.forEach((k) => store.removeItem(k));
        }
        window.sessionStorage.setItem(SIGNED_OUT_FLAG, "1");
      } catch {
        /* ignore */
      }
      window.location.replace(to);
    }
  }
}

/** Stable callback wrapper for components that only need sign-out. */
export function useSignOut() {
  return useCallback(() => signOut(), []);
}

